import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CaretDown, CaretRight, ArrowsClockwise, ArrowClockwise, Skull, CheckCircle, XCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";

/**
 * Collapsible admin panel that lists every Peppol sandbox ping and every
 * pending / dead-lettered Fawtara retry-queue row. Lives under the
 * E-Invoices table on the Oman Compliance page.
 *
 * Sandbox pings prove the UBL XML shape is accepted by OTA before we
 * start real filings. Retry queue keeps flaky-network submissions from
 * ever silently dropping.
 */
export default function FawtaraOpsPanel() {
  const [open, setOpen] = useState(false);
  const [pings, setPings] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, q] = await Promise.all([
        api.get("/oman/sandbox-pings"),
        api.get("/oman/retry-queue"),
      ]);
      setPings(p.data || []);
      setQueue(q.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load Fawtara ops data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open && pings.length === 0 && queue.length === 0) load(); }, [open]);

  const retryNow = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/oman/retry-queue/${id}/retry-now`);
      toast[data.ok ? "success" : "warning"](
        data.ok ? "Retry succeeded — invoice submitted" : `Retry still failing: ${data.result?.reason || "unknown"}`
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const deadLetter = async (id) => {
    if (!window.confirm("Park this row in the dead-letter state? The cron will stop retrying it.")) return;
    setBusy(id);
    try {
      await api.post(`/oman/retry-queue/${id}/dead-letter`);
      toast.success("Row dead-lettered");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const queuedCount = queue.filter((r) => r.status === "queued").length;
  const deadCount = queue.filter((r) => r.status === "dead_letter").length;
  const succeededPings = pings.filter((p) => p.accepted).length;

  return (
    <div className="border border-zinc-200 mt-8" data-testid="fawtara-ops-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="fawtara-ops-toggle"
        className="w-full flex items-center justify-between px-4 py-2.5 bg-zinc-50 hover:bg-zinc-100 text-left"
      >
        <div className="flex items-center gap-2">
          {open ? <CaretDown size={12} className="text-zinc-500" /> : <CaretRight size={12} className="text-zinc-500" />}
          <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-700">
            Fawtara ops · sandbox pings + retry queue
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-500">
          <span className="px-1.5 py-0.5 border bg-emerald-50 text-emerald-700 border-emerald-300">
            SANDBOX PINGS · {succeededPings} / {pings.length} OK
          </span>
          <span className="px-1.5 py-0.5 border bg-amber-50 text-amber-700 border-amber-300">
            QUEUED · {queuedCount}
          </span>
          <span className={`px-1.5 py-0.5 border ${deadCount > 0 ? "bg-red-50 text-[#D0021B] border-red-300" : "bg-zinc-100 text-zinc-500 border-zinc-300"}`}>
            DEAD-LETTER · {deadCount}
          </span>
        </div>
      </button>

      {open && (
        <div className="border-t border-zinc-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">Retry queue ({queue.length})</div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="fawtara-ops-refresh"
              className="text-[10px] font-mono text-zinc-500 hover:text-zinc-950 flex items-center gap-1 disabled:opacity-40"
            >
              <ArrowsClockwise size={11} /> {loading ? "LOADING…" : "REFRESH"}
            </button>
          </div>

          {queue.length === 0 ? (
            <div className="p-4 text-center text-xs font-mono text-zinc-400 border border-dashed border-zinc-200 mb-6">
              NO QUEUED RETRIES — HAPPY PATH
            </div>
          ) : (
            <table className="w-full text-sm mb-6" data-testid="retry-queue-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-[10px] font-mono tracking-[0.15em] uppercase text-zinc-500">
                  <th className="text-left px-3 py-2">Invoice</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Attempts</th>
                  <th className="text-left px-3 py-2">Next attempt</th>
                  <th className="text-left px-3 py-2">Last error</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((r) => {
                  const isDead = r.status === "dead_letter";
                  const statusCls = isDead
                    ? "bg-red-50 text-[#D0021B] border-red-300"
                    : r.status === "in_flight"
                      ? "bg-blue-50 text-blue-700 border-blue-300"
                      : r.status === "succeeded"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                        : "bg-amber-50 text-amber-700 border-amber-300";
                  return (
                    <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-3 py-2 font-mono text-xs">{r.invoice_no || r.invoice_id?.slice(-8)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${statusCls}`}>{r.status.toUpperCase()}</span>
                      </td>
                      <td className="px-3 py-2 text-right tnum text-xs">{r.attempts}</td>
                      <td className="px-3 py-2 text-xs text-zinc-500 whitespace-nowrap">
                        {r.next_attempt_at ? new Date(r.next_attempt_at).toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-500 truncate max-w-[240px]" title={r.last_error}>{r.last_error || "—"}</td>
                      <td className="px-3 py-2 flex gap-1.5 justify-end">
                        <button
                          type="button"
                          onClick={() => retryNow(r.id)}
                          disabled={busy === r.id || r.status === "succeeded" || r.status === "in_flight"}
                          data-testid={`retry-now-${r.id}`}
                          className="text-[10px] font-mono px-2 py-1 border border-zinc-950 hover:bg-zinc-950 hover:text-white flex items-center gap-1 disabled:opacity-30"
                        >
                          <ArrowClockwise size={10} /> RETRY NOW
                        </button>
                        {!isDead && r.status !== "succeeded" && (
                          <button
                            type="button"
                            onClick={() => deadLetter(r.id)}
                            disabled={busy === r.id}
                            data-testid={`dead-letter-${r.id}`}
                            className="text-[10px] font-mono px-2 py-1 border border-red-300 text-[#D0021B] hover:bg-[#D0021B] hover:text-white flex items-center gap-1 disabled:opacity-30"
                          >
                            <Skull size={10} /> DEAD-LETTER
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase mb-3">Sandbox pings ({pings.length})</div>
          {pings.length === 0 ? (
            <div className="p-4 text-center text-xs font-mono text-zinc-400 border border-dashed border-zinc-200">
              NO SANDBOX PINGS YET — click SANDBOX PING on any generated e-invoice above
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="sandbox-pings-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-[10px] font-mono tracking-[0.15em] uppercase text-zinc-500">
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Invoice</th>
                  <th className="text-left px-3 py-2">Mode</th>
                  <th className="text-left px-3 py-2">Result</th>
                  <th className="text-left px-3 py-2">Peppol Msg ID</th>
                  <th className="text-left px-3 py-2">By</th>
                </tr>
              </thead>
              <tbody>
                {pings.map((p) => (
                  <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-3 py-2 tnum text-xs text-zinc-500 whitespace-nowrap">
                      {new Date(p.sent_at).toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{p.invoice_no || p.invoice_id?.slice(-8)}</td>
                    <td className="px-3 py-2 text-xs uppercase text-zinc-500">{p.sandbox_mode}</td>
                    <td className="px-3 py-2">
                      {p.accepted ? (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 border bg-emerald-50 text-emerald-700 border-emerald-300 flex items-center gap-1 w-fit">
                          <CheckCircle size={10} /> ACCEPTED
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 border bg-red-50 text-[#D0021B] border-red-300 flex items-center gap-1 w-fit">
                          <XCircle size={10} /> REJECTED
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">{p.ack?.peppol_message_id || "—"}</td>
                    <td className="px-3 py-2 text-xs text-zinc-500 font-mono">{p.sent_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
